//frontend/dispatcher-client/src/hooks/usePersonnelPublicHolidaysData.js
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  fetchPersonnelPublicHolidays,
} from "../api/personnelApi";

import {
  loadPersonnelResource,
  readPersonnelCache,
  subscribePersonnelCache,
} from "../utils/personnelCache";

import {
  getPersonnelYearRanges,
} from "../lib/prefetchPersonnel";

/* =========================================================
 * CACHE
 * ======================================================= */

const readYearCache =
  (
    year
  ) => {
    const ranges =
      getPersonnelYearRanges(
        year
      );

    return {
      params:
        ranges.publicHolidays,

      cached:
        readPersonnelCache(
          "publicHolidays",
          ranges.publicHolidays
        ),
    };
  };

/* =========================================================
 * HOOK
 * ======================================================= */

export default function usePersonnelPublicHolidaysData(
  year
) {
  const initial =
    readYearCache(
      year
    );

  const [
    publicHolidays,
    setPublicHolidays,
  ] =
    useState(
      () =>
        initial.cached
          ?.data ||
        []
    );

  const [
    loading,
    setLoading,
  ] =
    useState(
      () =>
        !initial.cached
    );

  const [
    refreshing,
    setRefreshing,
  ] =
    useState(
      false
    );

  const [
    error,
    setError,
  ] =
    useState(
      null
    );

  const requestRef =
    useRef(0);

  const realtimeRefreshTimerRef =
    useRef(
      null
    );

  /* =======================================================
   * REFRESH
   * ===================================================== */

  const refresh =
    useCallback(
      async ({
        silent =
          true,
      } = {}) => {
        const requestId =
          requestRef.current +
          1;

        requestRef.current =
          requestId;

        const ranges =
          getPersonnelYearRanges(
            year
          );

        if (!silent) {
          setLoading(
            true
          );
        }

        setRefreshing(
          true
        );

        setError(
          null
        );

        try {
          const data =
            await loadPersonnelResource({
              resource:
                "publicHolidays",

              params:
                ranges.publicHolidays,

              force:
                true,

              loader:
                () =>
                  fetchPersonnelPublicHolidays(
                    ranges.publicHolidays
                  ),
            });

          if (
            requestRef.current !==
            requestId
          ) {
            return;
          }

          setPublicHolidays(
            data
          );

        } catch (
          refreshError
        ) {
          if (
            requestRef.current !==
            requestId
          ) {
            return;
          }

          console.error(
            "[PersonnelPublicHolidays] refresh failed:",
            refreshError
          );

          setError(
            refreshError
          );

        } finally {
          if (
            requestRef.current ===
            requestId
          ) {
            setLoading(
              false
            );

            setRefreshing(
              false
            );
          }
        }
      },
      [
        year,
      ]
    );

  /* =======================================================
   * YEAR CHANGE / INITIAL LOAD
   * ===================================================== */

  useEffect(
    () => {
      const {
        cached,
      } =
        readYearCache(
          year
        );

      if (cached) {
        setPublicHolidays(
          cached.data
        );

        setLoading(
          false
        );
      } else {
        setPublicHolidays(
          []
        );

        setLoading(
          true
        );
      }

      if (
        !cached ||
        cached.isStale
      ) {
        refresh({
          silent:
            !!cached,
        });
      } else {
        setRefreshing(
          false
        );

        setError(
          null
        );
      }
    },
    [
      year,
      refresh,
    ]
  );

  /* =======================================================
   * CACHE SYNC + REALTIME
   * ===================================================== */

  useEffect(
    () => {
      const unsubscribe =
        subscribePersonnelCache(
          (
            event
          ) => {
            if (
              event.resource !==
              "publicHolidays"
            ) {
              return;
            }

            if (
              event.stale ===
              true
            ) {
              const requestVersionAtEvent =
                requestRef.current;

              if (
                realtimeRefreshTimerRef.current
              ) {
                window.clearTimeout(
                  realtimeRefreshTimerRef.current
                );
              }

              realtimeRefreshTimerRef.current =
                window.setTimeout(
                  () => {
                    realtimeRefreshTimerRef.current =
                      null;

                    if (
                      requestRef.current !==
                      requestVersionAtEvent
                    ) {
                      return;
                    }

                    refresh({
                      silent:
                        true,
                    }).catch(
                      () => {
                        /*
                         * refresh() owns the error state.
                         */
                      }
                    );
                  },
                  60
                );

              return;
            }

            const {
              cached,
            } =
              readYearCache(
                year
              );

            if (
              cached
            ) {
              setPublicHolidays(
                cached.data
              );
            }
          }
        );

      return () => {
        unsubscribe();

        if (
          realtimeRefreshTimerRef.current
        ) {
          window.clearTimeout(
            realtimeRefreshTimerRef.current
          );

          realtimeRefreshTimerRef.current =
            null;
        }
      };
    },
    [
      year,
      refresh,
    ]
  );

  return {
    publicHolidays,

    loading,
    refreshing,
    error,

    refresh,
  };
}